import { useContext } from 'react'
import Face4OutlinedIcon from '@mui/icons-material/Face4Outlined'
import { DeccaContext } from '../../StateManagment/DeccaContext.js'
import DeepAnalysisAIContext from '../../StateManagment/DeepAnalysisAIContext'
import DeepAnalysisPeriodContext from '../../StateManagment/DeepAnalysisPeriodContext'
import periodsForAI from '../../helpers/compareHelpers/periodsForAI'
import { Auth } from '../../StateManagment/Auth'
import newConversation from '../../agents/newConversation.js'
import { db, timestamp } from '../../firebase/config'
import { client } from '../../hooks/Client'
import { useFirestoreOrderBy } from '../../firebase/useFirestore'
import uuid from 'react-uuid'

/**
 * AI section of a deep-analysis page: starts a Decca conversation pre-scoped
 * to the page's anchor entity (no selection step — the anchor IS the scope).
 *
 * Mirrors the Decca wiring of the old AIEffectAnalysis / AIMultipleResearchAnalysis
 * builders: same subjects and contextData shapes, so the existing Cloud Function
 * context providers (functions/ai/decca/subjectInfo/analytics/*) keep working.
 *
 * @param {object} props
 * @param {'effect' | 'research' | 'activity'} props.type - Anchor type of the page
 * @param {object} props.anchor - The anchor doc (OutputEffects, Research or Activities)
 */
const DeepAnalysisAISection = ({ type, anchor }) => {
    const [auth] = useContext(Auth)
    const {
        chatOpen,
        setChatOpen,
        setSelectedConversationID,
        setShowConversations,
        setSubject,
        setContextData,
    } = useContext(DeccaContext)

    const conversations = useFirestoreOrderBy('Conversations', 'Position', 'desc')

    // Summaries published by the page sections (spreiding, correlaties, …) —
    // sent along so Decca sees the same context as the researcher. Null keys
    // (e.g. a closed pair comparison) are dropped.
    const aiContext = useContext(DeepAnalysisAIContext)
    const pageContext = Object.fromEntries(
        Object.entries(aiContext?.pageContext || {}).filter(([, value]) => value != null)
    )
    const hasPageContext = Object.keys(pageContext).length > 0

    // The page period, for the research analysis (contract in
    // functions/utils/parseAnalysisPeriods.js). The effect analysis gets its
    // period through the page summaries, which carry a `periode` label.
    // AI-CONTEXT: See docs/features/deep-analysis-period.md
    const periods = periodsForAI(useContext(DeepAnalysisPeriodContext))

    const anchorTitle = type === 'effect'
        ? anchor.Effect
        : type === 'activity'
            ? anchor.Activity
            : anchor.Title

    const startAnalysis = async () => {
        const conversationID = uuid()
        const title = `Analyse van ${anchorTitle}`
        const message = `<h2>Hallo ${auth.ForName}</h2><p>Zal ik een algemene analyse doen van ${anchorTitle} of wil je iets specifiekers weten?</p>`

        if (type === 'effect') {
            setSubject('AIEffectAnalysis')
            setContextData({
                effectId: anchor.ID,
                effectTitle: anchor.Effect,
                ...(hasPageContext ? { pageContext } : {}),
            })
        } else if (type === 'activity') {
            setSubject('AIActivityAnalysis')
            setContextData({ activityId: anchor.ID, activityTitle: anchor.Activity })
        } else {
            setSubject('ResearchAnalysis')
            setContextData({ selectedResearch: [anchor.ID], ...(periods ? { periods } : {}) })
        }

        setChatOpen(chatOpen === false ? true : true)
        await newConversation(
            conversationID,
            title,
            message,
            conversations.length + 1,
            client,
            timestamp,
            uuid(),
            auth.ID,
            auth.ForName,
            db
        )
        setSelectedConversationID(conversationID)
        setShowConversations(false)
    }

    return (
        <div className='deep-analysis-section deep-analysis-ai'>
            <div className='deep-analysis-ai-text'>
                <div className='deep-analysis-section-kicker'>
                    <span className='deep-analysis-section-kicker-n' aria-hidden='true' />
                    Verdiepen
                </div>
                <h3 className='deep-analysis-section-title'>AI-analyse</h3>
                <p className='deep-analysis-section-desc'>
                    Laat Decca de resultaten van {anchorTitle ? `"${anchorTitle}"` : 'dit item'} analyseren
                    en stel vervolgvragen in het gesprek.
                </p>
            </div>
            <button type='button' className='deep-analysis-ai-button' onClick={startAnalysis}>
                <span className='deep-analysis-ai-button-face'>
                    <Face4OutlinedIcon />
                </span>
                Analyseer met Decca
            </button>
        </div>
    )
}

export default DeepAnalysisAISection
